import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useCnicFlag } from 'utils/featureFlags';
import {
  DEFAULT_ACTIVE_HURRICANES_SOURCE,
  DEFAULT_TROPICAL_CONDITIONS,
  useCreateOrgEventAutomationRule,
  useOrgEventAutomationRules,
  type CreateOrgEventAutomationRulePayload,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { NotificationType } from 'hooks/useNotification';
import {
  CNIC_HAWAII_AUTOMATION_RULE_NAME,
  CNIC_HAWAII_EVENT_REGION,
  CNIC_HAWAII_THRESHOLD_DESCRIPTION,
} from '../NotificationsTab/cnicHawaiiThreatNotification';

const ensureLocks = new Map<string, Promise<unknown>>();

const CNIC_SEEDED_RULES: Array<{
  name: string;
  type: string;
  thresholdDescription: string;
}> = [
  {
    name: CNIC_HAWAII_AUTOMATION_RULE_NAME,
    type: 'Tropical Storm',
    thresholdDescription: CNIC_HAWAII_THRESHOLD_DESCRIPTION,
  },
];

/**
 * Ensures the CNIC group has a tropical-storm cone rule so resource
 * checklists can be keyed by rule name.
 */
export function useEnsureCnicTropicalAutomationRule() {
  const cnicEnabled = useCnicFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const { data: rules, isSuccess } = useOrgEventAutomationRules({
    enabled: cnicEnabled && !!groupGuid,
  });
  const createRule = useCreateOrgEventAutomationRule();
  const attempted = useRef(false);

  useEffect(() => {
    if (!cnicEnabled || !groupGuid || !isSuccess || attempted.current) return;
    const existingNames = new Set(
      (rules ?? []).filter((rule) => !rule.archived).map((rule) => rule.name)
    );
    const missing = CNIC_SEEDED_RULES.filter(
      (seed) => !existingNames.has(seed.name)
    );
    if (missing.length === 0) {
      attempted.current = true;
      return;
    }

    const lockKey = `cnic-automation-rules:${groupGuid}`;
    if (ensureLocks.has(lockKey)) return;

    attempted.current = true;
    const notifyConfig = {
      notificationTypes: [NotificationType.IN_APP],
      userIds: userGuid ? [userGuid] : [],
      teamIds: [],
      priority: 'watch',
      titleTemplate: 'Threshold event: {eventName}',
      bodyTemplate:
        '{eventName}: {stormName} impacts {resourceCount} resources.',
    };

    const promise = Promise.all(
      missing.map((seed) => {
        const payload: CreateOrgEventAutomationRulePayload = {
          group_guid: groupGuid,
          name: seed.name,
          enabled: false,
          logic: 'all',
          source: DEFAULT_ACTIVE_HURRICANES_SOURCE,
          conditions: DEFAULT_TROPICAL_CONDITIONS,
          event_template: {
            severity: 'High',
            type: seed.type,
            region: CNIC_HAWAII_EVENT_REGION,
            thresholdDescription: seed.thresholdDescription,
          },
          notify_on_create: false,
          checklist_on_create: false,
          checklist_config: { items: [] },
          notify_config: notifyConfig,
          created_by_user_guid: userGuid ?? null,
        };
        return createRule.mutateAsync(payload);
      })
    )
      .catch((err) => {
        console.warn('[cnic] failed to ensure automation rules', err);
        attempted.current = false;
      })
      .finally(() => {
        ensureLocks.delete(lockKey);
      });

    ensureLocks.set(lockKey, promise);
  }, [cnicEnabled, groupGuid, userGuid, isSuccess, rules, createRule]);
}
